/**
 * ambassadorRef.js — capture + persist the TikTok-ambassador referral
 * tag on /start so the conversion that happens 60 seconds later
 * (or 30 days later, after the visitor comes back) still maps
 * to the right ambassador.
 *
 * Storage: sessionStorage (single-tab clean) + localStorage (30-day
 * cross-tab persistence). First-touch wins — if the user already
 * has a ref stored, a later ?ref= URL is ignored (industry standard
 * for "Alex sent me, then I clicked Blair's link, I still belong
 * to Alex"). Founder can flip this to last-touch by changing the
 * `firstTouchWins` flag.
 *
 * Honors a `?ref-clear=1` URL flag for testing — wipes both stores.
 */

const KEY = "trubeaute.ambassador_ref";
// Legacy key from the Creator-program era. We still read it on the
// first call so a TikTok visitor who landed pre-rebrand and bounced
// (their ?ref= sits in localStorage for up to 30 days) still
// attributes correctly after the rename. We migrate-and-delete on
// the first successful read so the old key never re-fires.
const LEGACY_KEY = "trubeaute.creator_ref";
const TTL_DAYS = 30;

const _now = () => Date.now();

const _writeBoth = (record) => {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(record));
  } catch {
    /* private mode etc. */
  }
  try {
    localStorage.setItem(KEY, JSON.stringify(record));
  } catch {
    /* iOS Safari private mode etc. */
  }
};

const _readEither = () => {
  // Session takes precedence (per-tab), then local (cross-tab).
  for (const store of [
    typeof sessionStorage !== "undefined" ? sessionStorage : null,
    typeof localStorage !== "undefined" ? localStorage : null,
  ]) {
    if (!store) continue;
    for (const k of [KEY, LEGACY_KEY]) {
      try {
        const raw = store.getItem(k);
        if (!raw) continue;
        const rec = JSON.parse(raw);
        if (!rec || !rec.handle) continue;
        // TTL check
        if (rec.t && _now() - rec.t > TTL_DAYS * 86_400_000) continue;
        // One-shot legacy migration — re-stamp under the new key
        // and drop the old one so this branch never fires again.
        if (k === LEGACY_KEY) {
          try { store.setItem(KEY, raw); } catch { /* ignore */ }
          try { store.removeItem(LEGACY_KEY); } catch { /* ignore */ }
        }
        return rec;
      } catch {
        /* fall through to next key / store */
      }
    }
  }
  return null;
};

const _clear = () => {
  for (const k of [KEY, LEGACY_KEY]) {
    try { sessionStorage.removeItem(k); } catch { /* ignore */ }
    try { localStorage.removeItem(k); } catch { /* ignore */ }
  }
};

/**
 * Inspect URL params. If ?ref= is present (and first-touch policy
 * allows), persist it. Returns the active ref handle for the page
 * (whether newly captured or previously stored).
 */
export const captureAmbassadorRefFromUrl = ({ firstTouchWins = true } = {}) => {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  if (params.get("ref-clear") === "1") {
    _clear();
  }
  const fresh = (params.get("ref") || "").toLowerCase().trim().slice(0, 32);
  const existing = _readEither();
  if (fresh) {
    if (existing && firstTouchWins) {
      // First-touch wins; keep the original
      return existing.handle;
    }
    _writeBoth({ handle: fresh, t: _now() });
    return fresh;
  }
  return existing?.handle || null;
};

export const getStoredAmbassadorRef = () => _readEither()?.handle || null;

export const clearStoredAmbassadorRef = () => _clear();
